import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, MapPin, Coins, Utensils, Users, Ticket, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import SponsorStrip from "@/components/SponsorStrip";
import BlindStructureCard from "@/components/BlindStructureCard";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [buying, setBuying] = useState(false);
  const [waitlistInfo, setWaitlistInfo] = useState(null);
  const [myReg, setMyReg] = useState(null);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => setEvent(r.data)).catch(() => navigate("/events"));
  }, [id, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/my/waitlist").then((r) => setWaitlistInfo(r.data.find((w) => w.event_id === id) || null)).catch(() => {});
    api.get("/my/registrations").then((r) => setMyReg(r.data.find((g) => g.event_id === id && (["paid", "registered"].includes(g.payment_status))) || null)).catch(() => {});
  }, [user, id]);

  const handleWaitlist = async () => {
    if (!user) { toast.error("Please log in to join the waitlist"); navigate("/login"); return; }
    try {
      await api.post(`/events/${id}/waitlist`);
      const r = await api.get("/my/waitlist");
      setWaitlistInfo(r.data.find((w) => w.event_id === id) || null);
      toast.success("You're on the waitlist — we'll email you if a seat frees up.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join waitlist");
    }
  };

  const handleBuy = async () => {
    if (!user) {
      toast.error("Please log in to register for a seat");
      navigate("/login");
      return;
    }
    setBuying(true);
    try {
      const { data } = await api.post(`/events/${id}/register`);
      setMyReg(data);
      setWaitlistInfo(null);
      const r = await api.get(`/events/${id}`);
      setEvent(r.data);
      toast.success("You're registered — your seat is confirmed!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not register for this event");
    } finally {
      setBuying(false);
    }
  };

  if (!event) return <div className="min-h-[60vh] flex items-center justify-center text-gold animate-pulse">Loading event…</div>;

  const dateStr = new Date(event.date + "T00:00:00").toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const seatsLeft = event.seats_available !== undefined ? event.seats_available : Math.max(0, (event.seats_total || 0) - (event.seats_sold || 0));
  const canClaim = waitlistInfo && waitlistInfo.status === "promoted";

  return (
    <div data-testid="event-detail-page">
      <section className="relative h-[42vh] min-h-[320px] overflow-hidden">
        <img src={event.image} alt={event.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-10">
          <Link to="/events" className="inline-flex items-center gap-2 text-slate-300 hover:text-gold mb-4 text-sm" data-testid="back-to-events"><ArrowLeft className="w-4 h-4" /> All Events</Link>
          {event.featured && <span className="w-fit bg-gold text-navy text-[10px] font-bold uppercase tracking-widest px-3 py-1 mb-3">Featured</span>}
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white tracking-tight">{event.title}</h1>
          {event.subtitle && <p className="font-display text-xl md:text-2xl text-gold-light mt-2" data-testid="event-detail-subtitle">{event.subtitle}</p>}
          <p className="text-gold mt-2 flex items-center gap-2"><MapPin className="w-4 h-4" /> {event.venue_name} · {event.season}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: Calendar, label: "Date", value: dateStr },
              { icon: Clock, label: "Start", value: event.time },
              { icon: Coins, label: "Buy-in", value: `$${event.buy_in || 0}` },
              { icon: Ticket, label: "Game", value: event.game_type },
            ].map((s, i) => (
              <div key={i} className="bg-navy-paper border border-slate-800 p-4">
                <s.icon className="w-5 h-5 text-gold mb-2" />
                <div className="text-slate-500 text-[10px] uppercase tracking-widest">{s.label}</div>
                <div className="text-white text-sm font-medium mt-0.5">{s.value}</div>
              </div>
            ))}
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold text-white mb-3">About This Event</h2>
            <p className="text-slate-300 leading-relaxed">{event.description}</p>
          </div>

          <BlindStructureCard structure={event.blind_structure_detail} />

          <div className="grid sm:grid-cols-2 gap-6">
            {event.food && (
              <div className="bg-navy-paper border border-slate-800 p-6" data-testid="event-food-card">
                <Utensils className="w-6 h-6 text-gold mb-3" />
                <h3 className="font-display text-lg font-bold text-white">Food & Drink</h3>
                <p className="text-slate-400 text-sm mt-2">{event.food}</p>
              </div>
            )}
            <div className="bg-navy-paper border border-slate-800 p-6">
              <MapPin className="w-6 h-6 text-gold mb-3" />
              <h3 className="font-display text-lg font-bold text-white">Location</h3>
              <p className="text-slate-400 text-sm mt-2">{event.address}</p>
              {event.venue_website && (
                <a href={event.venue_website} target="_blank" rel="noreferrer" className="text-gold text-sm underline mt-2 inline-block" data-testid="event-venue-website">Visit venue website →</a>
              )}
            </div>
          </div>

          <div className="border border-white/10 overflow-hidden">
            <iframe
              title="map"
              data-testid="event-map"
              className="w-full h-72"
              loading="lazy"
              src={`https://www.google.com/maps?q=${encodeURIComponent(event.map_query || event.address)}&output=embed`}
            />
          </div>
        </div>

        {/* REGISTRATION CARD */}
        <div className="lg:col-span-1">
          <div className="sticky top-28 bg-navy-paper border border-gold/30 p-7 drop-shadow-[0_0_25px_rgba(212,175,55,0.08)]">
            <div className="text-slate-400 text-xs uppercase tracking-widest">Tournament Seat</div>
            <div className="font-display text-3xl font-bold text-gold mt-1">Register to Play</div>

            <div className="mt-6 flex items-center justify-between text-sm border-t border-white/10 pt-4">
              <span className="text-slate-400 flex items-center gap-1.5"><Users className="w-4 h-4" /> Seats remaining</span>
              <span className="text-white font-semibold">{seatsLeft} / {event.seats_total}</span>
            </div>

            {myReg ? (
              <div className="w-full mt-6 border-2 border-gold text-gold text-center font-bold uppercase tracking-widest py-4" data-testid="registered-badge">You're Registered</div>
            ) : seatsLeft === 0 && !canClaim ? (
              <Button
                onClick={handleWaitlist}
                disabled={!!waitlistInfo}
                data-testid="join-waitlist-btn"
                className="w-full mt-6 border-2 border-gold bg-transparent text-gold hover:bg-gold hover:text-navy rounded-none font-bold uppercase tracking-widest py-6 disabled:opacity-60"
              >
                {waitlistInfo ? `You're #${waitlistInfo.position} on the Waitlist` : "Join Waitlist"}
              </Button>
            ) : (
              <Button
                onClick={handleBuy}
                disabled={buying}
                data-testid="buy-seat-btn"
                className="w-full mt-6 bg-gold text-navy hover:bg-gold-light rounded-none font-bold uppercase tracking-widest py-6 disabled:opacity-50"
              >
                {buying ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Registering…</> : canClaim ? "Claim Your Seat" : "Register for Seat"}
              </Button>
            )}
            {canClaim && <p className="text-gold text-xs mt-3 text-center" data-testid="claim-notice">A seat is being held for you — claim it before it passes to the next player.</p>}
            {myReg && <p className="text-slate-500 text-xs mt-3 text-center">Manage your seat from <Link to="/dashboard" className="text-gold underline">My Legends</Link>.</p>}
            {!myReg && seatsLeft === 0 && !canClaim && <p className="text-slate-500 text-xs mt-3 text-center">Event full — join the waitlist and we'll hold the next free seat for you.</p>}
            {!user && <p className="text-slate-500 text-xs mt-3 text-center">You'll need to <Link to="/login" className="text-gold underline">log in</Link> to register.</p>}
            <p className="text-slate-600 text-[11px] mt-4 text-center">Seats can be cancelled up to 24h before start.</p>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <SponsorStrip variant="event" testId="event-sponsor-strip" />
      </div>
    </div>
  );
}
